import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode
} from 'react'
import { logger } from '../lib/logger'
import { captureError } from '../lib/sentry'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  type User as FirebaseUser
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions, messagingPromise } from '../firebase'
import {
  setupForegroundMessageHandler,
  hasNotificationsEnabled,
  getFCMToken,
  saveFCMToken
} from '../services/notifications/fcm.service'
import { loginLimiter, passwordResetLimiter, registerLimiter } from '../utils/rateLimit'

/**
 * Mask email for privacy in logs
 * e.g., "john.doe@example.com" -> "j***e@example.com"
 */
function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return '***@***'
  if (localPart.length <= 2) return `${localPart[0]}***@${domain}`
  return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`
}

/**
 * Extract error message safely from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'Unknown error'
}

// Rate limiting callable functions
const checkRateLimitFn = httpsCallable<
  { action: string; identifier: string },
  { allowed: boolean; remainingAttempts: number; waitSeconds: number | null; message?: string }
>(functions, 'checkRateLimit')

const recordRateLimitAttemptFn = httpsCallable<
  { action: string; identifier: string },
  { recorded: boolean }
>(functions, 'recordRateLimitAttempt')

const clearRateLimitFn = httpsCallable<
  { action: string; identifier: string },
  { cleared: boolean }
>(functions, 'clearRateLimit')

/** Two-factor authentication method type */
export type TwoFactorMethod = 'sms' | 'totp'

/** SMS notification preferences */
export interface SMSNotificationPreferences {
  shiftReminders: boolean
  clockOutReminders: boolean
  approvalNotifications: boolean
  securityAlerts: boolean
}

/** User data stored in Firestore */
export interface UserData {
  uid: string
  email: string
  name: string
  phone?: string
  role: 'admin' | 'employee'
  approved: boolean
  ratePerHour: number
  type: string
  status: 'pending' | 'active' | 'inactive'
  createdAt: ReturnType<typeof serverTimestamp> | null
  approvedAt: ReturnType<typeof serverTimestamp> | null
  approvedBy: string | null
  fcmToken: string | null
  // Two-Factor Authentication
  twoFactorEnabled?: boolean
  twoFactorMethods?: TwoFactorMethod[]
  twoFactorSetupRequired?: boolean
  phoneVerified?: boolean
  totpVerified?: boolean
  recoveryCodesCount?: number
  // SMS Notification Preferences
  smsNotificationsEnabled?: boolean
  smsNotificationTypes?: SMSNotificationPreferences
}

/** Result of authentication operations */
interface AuthSuccessResult {
  success: true
  user: FirebaseUser
  userData: UserData | null
  requires2FA?: false
}

interface Auth2FARequiredResult {
  success: true
  requires2FA: true
  userId: string
  methods: TwoFactorMethod[]
}

interface AuthErrorResult {
  success: false
  error: string
}

type AuthResult = AuthSuccessResult | Auth2FARequiredResult | AuthErrorResult

/** Simple result for operations without user data */
interface SimpleSuccessResult {
  success: true
}

interface SimpleErrorResult {
  success: false
  error: string
}

type SimpleResult = SimpleSuccessResult | SimpleErrorResult

/** Auth context value */
interface AuthContextValue {
  user: FirebaseUser | null
  userData: UserData | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  isApproved: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<AuthResult>
  register: (email: string, password: string, name: string, phone?: string, employeeType?: 'employee' | 'self-employed') => Promise<AuthResult>
  logout: () => Promise<SimpleResult>
  resetPassword: (email: string) => Promise<SimpleResult>
  refreshUserData: () => Promise<UserData | null>
  clearError: () => void
}

/** Props for AuthProvider */
interface AuthProviderProps {
  children: ReactNode
  initialUser?: FirebaseUser | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Result of password validation */
interface PasswordValidationResult {
  valid: boolean
  error: string | null
}

/**
 * Validate password strength
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character (!@#$%^&*(),.?":{}|<>)
 */
function validatePassword(password: string): PasswordValidationResult {
  if (password.length < 12) {
    return {
      valid: false,
      error: 'Password must be at least 12 characters long.'
    }
  }

  if (!/[A-Z]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one uppercase letter.'
    }
  }

  if (!/[a-z]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one lowercase letter.'
    }
  }

  if (!/[0-9]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one number.'
    }
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return {
      valid: false,
      error: 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>).'
    }
  }

  return { valid: true, error: null }
}

/**
 * Get user-friendly error message from Firebase auth error code
 */
function getAuthErrorMessage(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered.'
    case 'auth/invalid-email':
      return 'Invalid email address.'
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.'
    case 'auth/weak-password':
      return 'Password must be at least 12 characters and include uppercase, lowercase, number, and special character.'
    case 'auth/user-disabled':
      return 'This account has been disabled.'
    case 'auth/user-not-found':
      return 'No account found with this email.'
    case 'auth/wrong-password':
      return 'Incorrect password.'
    case 'auth/invalid-credential':
      return 'Invalid email or password.'
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.'
    default:
      return 'An error occurred. Please try again.'
  }
}

export function AuthProvider({ children, initialUser = null }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<FirebaseUser | null>(initialUser)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch user data from Firestore
  const fetchUserData = useCallback(async (uid: string): Promise<UserData | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        return { uid, ...userDoc.data() } as UserData
      }
      return null
    } catch (err: unknown) {
      if (import.meta.env.DEV) {
        console.error('Error fetching user data:', err)
      }
      return null
    }
  }, [])

  // Track foreground message handler unsubscribe
  const foregroundUnsubscribeRef = useRef<(() => void) | null>(null)

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser)
        const data = await fetchUserData(firebaseUser.uid)
        setUserData(data)
      } else {
        setUser(null)
        setUserData(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [fetchUserData])

  // Set up FCM when user is authenticated and has notifications enabled
  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    // Capture user.uid at start to avoid stale closure
    const currentUserId = user?.uid

    async function setupFCM(): Promise<void> {
      if (!user || !currentUserId || !hasNotificationsEnabled()) {
        return
      }

      try {
        const messaging = await messagingPromise

        // Check for abort after first await
        if (signal.aborted || !messaging) {
          return
        }

        // Set up foreground message handler
        if (foregroundUnsubscribeRef.current) {
          foregroundUnsubscribeRef.current()
        }

        // Check again before setting up handler
        if (signal.aborted) {
          return
        }

        foregroundUnsubscribeRef.current = setupForegroundMessageHandler(
          messaging,
          (notification) => {
            // Show browser notification for foreground messages
            if (notification.title) {
              new Notification(notification.title, {
                body: notification.body,
                icon: '/icons/icon-192x192.png'
              })
            }
            logger.info('Foreground notification received', { notification })
          }
        )

        // Refresh FCM token if needed (tokens can expire or change)
        const tokenResult = await getFCMToken(messaging)

        // Final abort check before Firestore write
        if (signal.aborted) {
          return
        }

        if (tokenResult.success) {
          // Save token to Firestore using captured userId
          await saveFCMToken(currentUserId, tokenResult.token)
          logger.info('FCM token refreshed on auth state change')
        }
      } catch (error) {
        // Ignore AbortError as it's expected during cleanup
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        logger.error('Error setting up FCM', error as Error)
        // Report to Sentry with FCM context
        captureError(
          error instanceof Error ? error : new Error('FCM setup failed'),
          {
            component: 'AuthContext',
            operation: 'setupFCM',
            userId: currentUserId,
            hasNotificationsEnabled: hasNotificationsEnabled()
          }
        )
      }
    }

    setupFCM()

    return () => {
      abortController.abort()
      if (foregroundUnsubscribeRef.current) {
        foregroundUnsubscribeRef.current()
        foregroundUnsubscribeRef.current = null
      }
    }
  }, [user])

  // Login with server-side rate limiting
  const login = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    setError(null)

    // Check server-side rate limit before attempting login
    try {
      const rateLimitResult = await checkRateLimitFn({ action: 'login', identifier: email })
      const { allowed, message } = rateLimitResult.data

      if (!allowed) {
        const errorMessage = message || 'Too many login attempts. Please try again later.'
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    } catch {
      // SECURITY FIX: If server rate limit check fails, use client-side fallback (fail CLOSED)
      if (import.meta.env.DEV) {
        console.warn('Server rate limit check unavailable, using client-side fallback')
      }
      const localCheck = loginLimiter.check()
      if (!localCheck.allowed) {
        const errorMessage = `Too many login attempts. Please try again in ${Math.ceil((localCheck.waitSeconds || 60) / 60)} minutes.`
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      const data = await fetchUserData(result.user.uid)

      // Check if 2FA is enabled
      if (data?.twoFactorEnabled && data.twoFactorMethods && data.twoFactorMethods.length > 0) {
        // Sign out immediately - user needs to verify 2FA first
        await signOut(auth)
        setUser(null)
        setUserData(null)

        // Clear rate limit on successful password auth (server + client)
        loginLimiter.recordSuccess()
        clearRateLimitFn({ action: 'login', identifier: email }).catch((err: unknown) => {
          logger.warn('Failed to clear rate limit after login', { error: getErrorMessage(err), action: 'login', email: maskEmail(email) })
        })

        // Return 2FA required result with user info for verification
        return {
          success: true,
          requires2FA: true,
          userId: result.user.uid,
          methods: data.twoFactorMethods
        }
      }

      // No 2FA required - complete login normally
      setUserData(data)

      // Clear rate limit on successful login (server + client)
      loginLimiter.recordSuccess()
      clearRateLimitFn({ action: 'login', identifier: email }).catch((err: unknown) => {
        logger.warn('Failed to clear rate limit after login', { error: getErrorMessage(err), action: 'login', email: maskEmail(email) })
      })

      return { success: true, user: result.user, userData: data, requires2FA: false }
    } catch (err: unknown) {
      // Record failed attempt for rate limiting (server + client)
      loginLimiter.recordAttempt()
      recordRateLimitAttemptFn({ action: 'login', identifier: email }).catch((rateLimitErr: unknown) => {
        logger.warn('Failed to record login attempt for rate limiting', { error: getErrorMessage(rateLimitErr), action: 'login', email: maskEmail(email) })
      })

      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : ''
      const errorMessage = getAuthErrorMessage(code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [fetchUserData])

  // Register with server-side rate limiting
  const register = useCallback(async (
    email: string,
    password: string,
    name: string,
    phone?: string,
    employeeType: 'employee' | 'self-employed' = 'employee'
  ): Promise<AuthResult> => {
    setError(null)

    // Validate password strength before proceeding
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.valid) {
      const errorMessage = passwordValidation.error || 'Invalid password.'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }

    // Check server-side rate limit before attempting registration
    try {
      const rateLimitResult = await checkRateLimitFn({ action: 'register', identifier: email })
      const { allowed, message } = rateLimitResult.data

      if (!allowed) {
        const errorMessage = message || 'Too many registration attempts. Please try again later.'
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    } catch {
      // SECURITY FIX: If server rate limit check fails, use client-side fallback (fail CLOSED)
      if (import.meta.env.DEV) {
        console.warn('Server rate limit check unavailable, using client-side fallback')
      }
      const localCheck = registerLimiter.check()
      if (!localCheck.allowed) {
        const errorMessage = `Too many registration attempts. Please try again in ${Math.ceil((localCheck.waitSeconds || 60) / 60)} minutes.`
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)

      // Create user document in Firestore
      const userDocData: UserData = {
        uid: result.user.uid,
        email,
        name,
        phone,
        role: 'employee',
        approved: false,
        ratePerHour: 0,
        type: employeeType,
        status: 'pending',
        createdAt: serverTimestamp(),
        approvedAt: null,
        approvedBy: null,
        fcmToken: null
      }

      await setDoc(doc(db, 'users', result.user.uid), userDocData)

      setUserData(userDocData)
      registerLimiter.recordSuccess()
      return { success: true, user: result.user, userData: userDocData }
    } catch (err: unknown) {
      // Record failed attempt for rate limiting (server + client)
      registerLimiter.recordAttempt()
      recordRateLimitAttemptFn({ action: 'register', identifier: email }).catch((rateLimitErr: unknown) => {
        logger.warn('Failed to record registration attempt for rate limiting', { error: getErrorMessage(rateLimitErr), action: 'register', email: maskEmail(email) })
      })

      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : ''
      const errorMessage = getAuthErrorMessage(code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Logout
  const logout = useCallback(async (): Promise<SimpleResult> => {
    setError(null)
    try {
      await signOut(auth)
      setUser(null)
      setUserData(null)
      return { success: true }
    } catch {
      const errorMessage = 'Failed to logout. Please try again.'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Reset password with server-side rate limiting
  const resetPassword = useCallback(async (email: string): Promise<SimpleResult> => {
    setError(null)

    // Check server-side rate limit before attempting password reset
    try {
      const rateLimitResult = await checkRateLimitFn({ action: 'passwordReset', identifier: email })
      const { allowed, message } = rateLimitResult.data

      if (!allowed) {
        const errorMessage = message || 'Too many password reset attempts. Please try again later.'
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    } catch {
      // SECURITY FIX: If server rate limit check fails, use client-side fallback (fail CLOSED)
      if (import.meta.env.DEV) {
        console.warn('Server rate limit check unavailable, using client-side fallback')
      }
      const localCheck = passwordResetLimiter.check()
      if (!localCheck.allowed) {
        const errorMessage = `Too many password reset attempts. Please try again in ${Math.ceil((localCheck.waitSeconds || 60) / 60)} minutes.`
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    }

    try {
      await sendPasswordResetEmail(auth, email)
      passwordResetLimiter.recordSuccess()
      return { success: true }
    } catch (err: unknown) {
      // Record attempt for rate limiting (server + client)
      passwordResetLimiter.recordAttempt()
      recordRateLimitAttemptFn({ action: 'passwordReset', identifier: email }).catch((rateLimitErr: unknown) => {
        logger.warn('Failed to record password reset attempt for rate limiting', { error: getErrorMessage(rateLimitErr), action: 'passwordReset', email: maskEmail(email) })
      })

      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : ''
      const errorMessage = getAuthErrorMessage(code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Refresh user data
  const refreshUserData = useCallback(async (): Promise<UserData | null> => {
    if (user) {
      const data = await fetchUserData(user.uid)
      setUserData(data)
      return data
    }
    return null
  }, [user, fetchUserData])

  // Memoize derived values
  const isAuthenticated = useMemo(() => !!user, [user])
  const isApproved = useMemo(() => userData?.approved === true, [userData])
  // DEMO_MODE: When enabled, treat all users as employees (hides admin features)
  const isAdmin = useMemo(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') return false
    return userData?.role === 'admin'
  }, [userData])
  const clearError = useCallback(() => setError(null), [])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextValue>(() => ({
    user,
    userData,
    loading,
    error,
    isAuthenticated,
    isApproved,
    isAdmin,
    login,
    register,
    logout,
    resetPassword,
    refreshUserData,
    clearError
  }), [
    user,
    userData,
    loading,
    error,
    isAuthenticated,
    isApproved,
    isAdmin,
    login,
    register,
    logout,
    resetPassword,
    refreshUserData,
    clearError
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
